import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { MultiplexPanelCellRuleSnapshot, MultiplexPanelCellRules } from 'interfaces/study';

export const panelCellRuleMarkerIds = (panel?: MultiplexPanelView | null): string[] =>
  (panel?.channels ?? []).flatMap((channel) =>
    channel.saved && channel.saved.channelModality !== 'dna' ? [channel.saved.analysisName] : []
  );

export const cellRuleRevisionLabel = (revision: { name?: string | null; revision?: number }) =>
  revision.name?.trim() || `Revision ${revision.revision ?? 1}`;

export const revisionsForPanel = (
  entries: MultiplexPanelCellRules[] | null | undefined,
  panelId: string | null
): MultiplexPanelCellRules[] =>
  (entries ?? [])
    .filter((entry) => (entry?.panelId ?? null) === panelId)
    .sort(
      (left, right) =>
        (right.revision ?? 1) - (left.revision ?? 1) ||
        String(right.savedAt ?? '').localeCompare(String(left.savedAt ?? ''))
    );

const cellTypeLabel = (path: string) => (path.split('.').pop() || path).replace(/_/g, ' ');

const blockedReuse = (unsupportedReason: string) => ({
  cellTypeIds: [] as string[],
  cellRules: [] as MinimalCellRule[],
  omittedMarkerIds: [] as string[],
  unsupportedReason,
});

/**
 * Turns a saved revision back into editor rows. A saved RNA rule stores only its RNA predicates plus
 * `sourceCellTypePath`; the editor expresses that source by repeating the source row's mIF predicates
 * on the target row, which `preparedCellRules` maps back to the same source.
 */
export const cellRulesFromRevision = (revision: MultiplexPanelCellRules | undefined, markerIds: string[]) => {
  const allowed = new Set(markerIds);
  const omittedMarkerIds: string[] = [];
  const cellTypeIds: string[] = [];
  const cellRules: MinimalCellRule[] = [];
  const rows = (revision?.rules ?? []).filter((rule) => rule?.targetCellTypePath);
  const rowsByTarget = new Map(rows.map((rule) => [rule.targetCellTypePath, rule]));
  const duplicate = rows.find(
    (rule, index) => rows.findIndex((other) => other.targetCellTypePath === rule.targetCellTypePath) !== index
  );
  if (duplicate) {
    return blockedReuse(
      `This revision has more than one rule for ${cellTypeLabel(
        duplicate.targetCellTypePath
      )}. The rule editor cannot show them separately, so it cannot be reused.`
    );
  }
  for (const rule of rows) {
    let ruleValues: Record<string, number> = rule.rules ?? {};
    if (rule.sourceCellTypePath) {
      const source = rowsByTarget.get(rule.sourceCellTypePath);
      if (!source || source.sourceCellTypePath) {
        return blockedReuse(
          `The ${cellTypeLabel(rule.targetCellTypePath)} rule applies only to ${cellTypeLabel(
            rule.sourceCellTypePath
          )} cells, which this revision does not define with marker rules, so it cannot be reused.`
        );
      }
      ruleValues = { ...source.rules, ...ruleValues };
    }
    cellTypeIds.push(rule.targetCellTypePath);
    Object.entries(ruleValues).forEach(([stainTypeId, ruleValue]) => {
      if (!allowed.has(stainTypeId)) {
        if (!omittedMarkerIds.includes(stainTypeId)) omittedMarkerIds.push(stainTypeId);
        return;
      }
      cellRules.push({
        cellTypeId: rule.targetCellTypePath,
        stainTypeId,
        ruleValue: ruleValue as CellRuleValue,
      });
    });
  }
  return { cellTypeIds, cellRules, omittedMarkerIds, unsupportedReason: null as string | null };
};

export const cellRuleRevisionSnapshots = (
  cellTypeIds: string[],
  rules: MinimalCellRule[],
  markerIds: string[]
): MultiplexPanelCellRuleSnapshot[] => {
  const allowed = new Set(markerIds);
  return cellTypeIds.flatMap((cellTypeId) => {
    const predicates: Record<string, number> = {};
    rules.forEach((rule) => {
      if (rule.cellTypeId !== cellTypeId || !allowed.has(rule.stainTypeId)) return;
      predicates[rule.stainTypeId] = rule.ruleValue;
    });
    return Object.values(predicates).some((ruleValue) => ruleValue !== CellRuleValue.Indifferent)
      ? [{ targetCellTypePath: cellTypeId, rules: predicates }]
      : [];
  });
};

export const sameCellRuleRevisionSnapshots = (
  left: MultiplexPanelCellRuleSnapshot[],
  right: MultiplexPanelCellRuleSnapshot[]
) => JSON.stringify(left) === JSON.stringify(right);
